import * as Tone from 'tone';
import { BASE_NOTES, MUSIC_SCALES, HARMONY } from '../utils/constants.js';
import { createDrumKit } from '../audio/DrumKit.js';
import { createRoleSampler } from '../audio/sampleLibrary.js';
import { DRUM_LANES, drumNoteForRow } from '../audio/songFormat.js';
import { events, EVT } from '../utils/events.js';

/**
 * ╔══════════════════════════════════════════════════════════╗
 *  MUSIC MAKER — Piano-roll sequencer with drag-to-draw
 * ╚══════════════════════════════════════════════════════════╝
 *
 * Click / touch + drag horizontally → place variable-length notes
 * Click existing note (or right-click / long-press) → erase
 * Drag loop handles in the top bar → set loop region
 * Configurable bar count (1–64), octave range (1–5)
 * Per-track mute · snap-to-grid · space-bar play/stop
 * Full touch support for mobile (single-finger draw, long-press erase)
 */

/* ── Grid geometry ─────────────────────────────── */
const CELL_W_DESKTOP = 24;
const CELL_H_DESKTOP = 18;
const CELL_W_MOBILE  = 30;   // wider for fingers, ~12 cols visible on 375px
const CELL_H_MOBILE  = 22;   // taller for fingers
const HEADER_H  = 40;        // loop bar (14px) + beat labels (26px)
const LOOP_BAR_H = 14;
const PIANO_W_DESKTOP = 56;
const PIANO_W_MOBILE  = 44;
const GUTTER    = 1;

/** Detect mobile / coarse-pointer devices — cached per check */
let _mobileCache = null;
let _mobileCacheTime = 0;
function _isMobile() {
  const now = performance.now();
  if (_mobileCache !== null && now - _mobileCacheTime < 500) return _mobileCache;
  _mobileCache = window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;
  _mobileCacheTime = now;
  return _mobileCache;
}

/* ── Timing ────────────────────────────────────── */
const BEATS_PER_BAR  = 4;
const CELLS_PER_BEAT = 4;    // 16th-note resolution
const CELLS_PER_BAR  = BEATS_PER_BAR * CELLS_PER_BEAT; // 16

/* ── Limits ────────────────────────────────────── */
const MIN_BARS = 1;
const MAX_BARS = 64;
const DEFAULT_BARS = 8;
const MIN_OCT  = 1;
const MAX_OCT  = 5;
const DEFAULT_OCT = 3;

/* ── Track colour palette ──────────────────────── */
const PAL = [
  [100,220,160],[100,180,255],[255,180,100],[220,120,255],
  [255,220,100],[120,255,220],[255,120,160],[180,180,255],
  [160,255,120],[255,160,220],[120,200,200],[200,140,100],
  [140,220,255],[255,200,160],[180,120,220],[220,255,140],
  [200,100,160],[100,255,180],[255,140,140],[160,160,255],
  [255,100,200],
];
const tCol = (i, a = 0.7) => {
  const c = PAL[i % PAL.length];
  return `rgba(${c[0]},${c[1]},${c[2]},${a})`;
};

/** Inline SVG icon referencing the sprite in index.html */
const icon = name => `<svg class="icon" aria-hidden="true"><use href="#icon-${name}"/></svg>`;

/** Read UI design tokens once for canvas rendering */
function readTheme() {
  const cs = getComputedStyle(document.documentElement);
  const v = (name, fallback) => (cs.getPropertyValue(name) || '').trim() || fallback;
  return {
    bg:     v('--bg', '#04060f'),
    text:   v('--text', '#e6eaff'),
    accent: v('--accent', '#7aa2ff'),
    mono:   v('--font-mono', 'monospace'),
  };
}

/** '#rrggbb' → 'rgba(r,g,b,a)' */
function rgba(hex, a) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/* ════════════════════════════════════════════════════════ */

export class MusicMaker {
  constructor() {
    /** @type {{ speciesId:number, name:string, instrumentRole:string, bodyType:string, discovered:boolean, notes:{col:number,row:number,dur:number}[], muted:boolean }[]} */
    this.tracks = [];
    this.activeTrackIndex = 0;
    this.planetMusic = null;
    this.scaleNotes  = [];

    /* grid config */
    this._bars     = DEFAULT_BARS;
    this._octaves  = DEFAULT_OCT;
    this._gridCols = DEFAULT_BARS * CELLS_PER_BAR;
    this._gridRows = 0;
    this._snap     = 4;           // snap in cells (4 = quarter note)

    /* loop region (in columns) */
    this._loopStart   = 0;
    this._loopEnd     = this._gridCols;
    this._loopEnabled = true;

    /* playback */
    this._seq       = null;
    this._playing   = false;
    this._playCol   = 0;

    /* DOM / canvas */
    this._canvas    = null;  this._ctx    = null;
    this._pianoC    = null;  this._pianoX = null;
    this._scrollEl  = null;
    this._animId    = null;

    /* drag state — null when idle */
    this._drag = null;

    /* two-finger pan state (mobile) — null when idle */
    this._pan = null;

    /* draw / erase mode (mobile) */
    this._eraseMode = false;

    /* mobile settings panel open? */
    this._settingsOpen = false;

    /* bar selection & clipboard */
    this._selectedBar   = -1;       // -1 = none selected
    this._clipboardBar  = null;     // { notes: [...] } per track

    /* undo / redo — snapshots of notes + grid size */
    this._undoStack = [];
    this._redoStack = [];

    /* canvas theme (design tokens) */
    this._theme = readTheme();

    /* synths */
    this._synths = [];
    this._bound  = false;
    this._audio  = null;
  }

  /** Current cell width based on device */
  get _cellW() { return _isMobile() ? CELL_W_MOBILE : CELL_W_DESKTOP; }
  get _cellH() { return _isMobile() ? CELL_H_MOBILE : CELL_H_DESKTOP; }
  get _pianoW() { return _isMobile() ? PIANO_W_MOBILE : PIANO_W_DESKTOP; }

  /** Percussion tracks use drum lanes instead of pitch rows */
  _isPercTrack(ti) { return this.tracks[ti]?.instrumentRole === 'percussion'; }
  get _activeIsPerc() { return this._isPercTrack(this.activeTrackIndex); }
  /** Row count of the current grid view (drum lanes vs pitch rows) */
  get _viewRows() { return this._activeIsPerc ? DRUM_LANES.length : this._gridRows; }
  /** Label for a row in the current view */
  _rowLabel(row) {
    return this._activeIsPerc ? (DRUM_LANES[row]?.label || '') : (this.scaleNotes[row] || '');
  }

  /* ═══════════════════════════════════════
     PUBLIC API
     ═══════════════════════════════════════ */

  open(speciesEntries, planetMusic, audioEngine) {
    this.planetMusic = planetMusic;
    this._audio      = audioEngine;
    this._theme      = readTheme();
    this._buildScale();
    this._buildTracks(speciesEntries);
    this._syncUI();
    this._initCanvas();
    this._populateTrackLabels();
    this._populateTrackPills();
    this._createSynths();
    this._render();
    this._renderPiano();
    this._bindEvents();
  }

  close() {
    this.stop();
    this._disposeSynths();
    // Ensure transport is fully idle
    try {
      const transport = Tone.getTransport();
      transport.stop();
      transport.cancel();
      transport.position = 0;
    } catch (_) {}
    if (this._animId) { cancelAnimationFrame(this._animId); this._animId = null; }
  }

  /* ═══════════════════════════════════════
     SCALE  /  NOTE  MAPPING
     ═══════════════════════════════════════ */

  _buildScale() {
    const pm   = this.planetMusic;
    const ri   = BASE_NOTES.indexOf(pm.root);
    const sc   = MUSIC_SCALES[pm.scaleName] || MUSIC_SCALES.pentatonic;
    const base = pm.octave || 3;
    const rows = this._octaves * sc.length;
    this._gridRows = rows;

    const notes = [];
    for (let i = 0; i < rows; i++) {
      const deg    = i % sc.length;
      const octOff = Math.floor(i / sc.length);
      notes.push(BASE_NOTES[(ri + sc[deg]) % 12] + (base + octOff));
    }
    this.scaleNotes = notes.reverse();   // highest pitch = row 0
  }

  /* ═══════════════════════════════════════
     TRACKS
     ═══════════════════════════════════════ */

  _buildTracks(entries) {
    this.tracks = entries.map(e => ({
      speciesId:      e.speciesId,
      name:           e.name,
      instrumentRole: e.instrumentRole,
      bodyType:       e.bodyType,
      discovered:     !!e.discovered,
      notes:          [],           // { col, row, dur, vel? }
      muted:          false,
      solo:           false,
      volume:         0.8,          // 0–1, scales the track's gain node
    }));
    this.activeTrackIndex = 0;
    this._undoStack = [];
    this._redoStack = [];
    this._updateUndoButtons();
  }

  /** Gain node value for a track — 0.8 volume ≈ the old fixed 0.5 gain */
  _trackGain(t) { return 0.625 * (t?.volume ?? 0.8); }

  _populateTrackLabels() {
    const box = document.getElementById('mm-tracks');
    if (!box) return;
    box.innerHTML = '';
    const icons = {
      blob:'🫧', quadruped:'🦎', biped:'🧬',
      serpent:'🐍', floater:'🎈', insectoid:'🦗',
    };

    this.tracks.forEach((t, i) => {
      const el = document.createElement('div');
      el.className = 'mm-track-label' + (i === this.activeTrackIndex ? ' active' : '');
      el.innerHTML = `
        <div class="mm-track-row">
          <span class="mm-track-color" style="background:${tCol(i,1)}"></span>
          <span class="mm-track-icon">${t.discovered ? (icons[t.bodyType]||'👾') : icon('note')}</span>
          <span class="mm-track-name${t.discovered ? '' : ' mm-track-dim'}">${t.name}</span>
          <button class="mm-track-solo${t.solo?' solo':''}" title="Solo">S</button>
          <button class="mm-track-mute${t.muted?' muted':''}" title="Mute/Unmute">${icon(t.muted ? 'speaker-off' : 'speaker')}</button>
        </div>
        <input type="range" class="mm-track-vol" min="0" max="100"
               value="${Math.round((t.volume ?? 0.8) * 100)}" title="Track volume">
      `;
      el.querySelector('.mm-track-mute').addEventListener('click', ev => {
        ev.stopPropagation();
        t.muted = !t.muted;
        ev.currentTarget.innerHTML = icon(t.muted ? 'speaker-off' : 'speaker');
        ev.currentTarget.classList.toggle('muted', t.muted);
      });
      el.querySelector('.mm-track-solo').addEventListener('click', ev => {
        ev.stopPropagation();
        t.solo = !t.solo;
        ev.currentTarget.classList.toggle('solo', t.solo);
      });
      const vol = el.querySelector('.mm-track-vol');
      vol.addEventListener('click', ev => ev.stopPropagation());
      vol.addEventListener('pointerdown', ev => ev.stopPropagation());
      vol.addEventListener('input', ev => {
        t.volume = (parseInt(ev.target.value) || 0) / 100;
        const si = this._synths[i];
        if (si) si.gain.gain.rampTo(this._trackGain(t), 0.05);
      });
      el.addEventListener('click', () => {
        this.activeTrackIndex = i;
        box.querySelectorAll('.mm-track-label').forEach(l => l.classList.remove('active'));
        el.classList.add('active');
        // sync pills
        const pills = document.getElementById('mm-track-pills');
        if (pills) {
          pills.querySelectorAll('.mm-track-pill').forEach(p => p.classList.remove('active'));
          pills.children[i]?.classList.add('active');
        }
        this._onTrackSwitched();
      });
      box.appendChild(el);
    });
  }

  /** Populate the mobile-friendly horizontal track pill bar */
  _populateTrackPills() {
    const box = document.getElementById('mm-track-pills');
    if (!box) return;
    box.innerHTML = '';
    const icons = {
      blob:'🫧', quadruped:'🦎', biped:'🧬',
      serpent:'🐍', floater:'🎈', insectoid:'🦗',
    };

    this.tracks.forEach((t, i) => {
      const pill = document.createElement('div');
      pill.className = 'mm-track-pill' + (i === this.activeTrackIndex ? ' active' : '');
      pill.innerHTML = `
        <span class="mm-pill-dot" style="background:${tCol(i,1)}"></span>
        ${t.discovered ? (icons[t.bodyType]||'👾') : icon('note')}
        <span>${t.name}</span>
      `;
      pill.addEventListener('click', () => {
        this.activeTrackIndex = i;
        box.querySelectorAll('.mm-track-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        // also sync sidebar if visible
        const sidebar = document.getElementById('mm-tracks');
        if (sidebar) {
          sidebar.querySelectorAll('.mm-track-label').forEach(l => l.classList.remove('active'));
          sidebar.children[i]?.classList.add('active');
        }
        this._onTrackSwitched();
      });
      box.appendChild(pill);
    });

    // Scroll active pill into view
    requestAnimationFrame(() => {
      const active = box.querySelector('.mm-track-pill.active');
      if (active) active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    });
  }

  /** After the active track changes — the grid may switch between
   *  pitch rows and drum lanes, so resize + redraw both canvases. */
  _onTrackSwitched() {
    this._sizeCanvases();
    this._render();
    this._renderPiano();
  }

  /* ═══════════════════════════════════════
     UNDO  /  REDO
     ═══════════════════════════════════════ */

  /** Snapshot the editable state before a mutation. */
  _pushUndo() {
    const snap = this._snapshot();
    snap.key = JSON.stringify([snap.bars, snap.octaves, snap.notes]);
    // skip if nothing changed since the last snapshot (no-op gestures)
    const top = this._undoStack[this._undoStack.length - 1];
    if (top?.key === snap.key) return;
    this._undoStack.push(snap);
    if (this._undoStack.length > 60) this._undoStack.shift();
    this._redoStack.length = 0;
    this._updateUndoButtons();
  }

  _snapshot() {
    return {
      bars:    this._bars,
      octaves: this._octaves,
      notes:   this.tracks.map(t => t.notes.map(n => ({ ...n }))),
    };
  }

  _restore(snap) {
    this._bars     = snap.bars;
    this._gridCols = snap.bars * CELLS_PER_BAR;
    if (snap.octaves !== this._octaves) {
      this._octaves = snap.octaves;
      this._buildScale();
    }
    this._loopEnd   = Math.min(this._loopEnd, this._gridCols);
    this._loopStart = Math.min(this._loopStart, Math.max(0, this._loopEnd - CELLS_PER_BAR));
    this.tracks.forEach((t, i) => { t.notes = (snap.notes[i] || []).map(n => ({ ...n })); });
    if (this._selectedBar >= this._bars) this._selectedBar = -1;

    const bd = document.getElementById('mm-bars-display'); if (bd) bd.textContent = this._bars;
    const od = document.getElementById('mm-oct-display');  if (od) od.textContent = this._octaves;
    this._sizeCanvases(); this._updateLoopLabel(); this._updateBarActions();
    this._render(); this._renderPiano();
  }

  undo() {
    const snap = this._undoStack.pop();
    if (!snap) return;
    this._redoStack.push(this._snapshot());
    this._restore(snap);
    this._updateUndoButtons();
  }

  redo() {
    const snap = this._redoStack.pop();
    if (!snap) return;
    this._undoStack.push(this._snapshot());
    this._restore(snap);
    this._updateUndoButtons();
  }

  _updateUndoButtons() {
    const u = document.getElementById('mm-undo');
    const r = document.getElementById('mm-redo');
    if (u) u.disabled = this._undoStack.length === 0;
    if (r) r.disabled = this._redoStack.length === 0;
  }

  /* ═══════════════════════════════════════
     NOTE  HELPERS
     ═══════════════════════════════════════ */

  /** Find the note (if any) occupying (col, row) in the given track. */
  _noteAt(track, col, row) {
    for (const n of track.notes) {
      if (n.row === row && col >= n.col && col < n.col + n.dur) return n;
    }
    return null;
  }

  /** Insert a note, removing any overlapping notes on the same row. */
  _addNote(track, col, row, dur) {
    track.notes = track.notes.filter(n =>
      n.row !== row || n.col + n.dur <= col || n.col >= col + dur
    );
    const note = { col, row, dur };
    track.notes.push(note);
    return note;
  }

  _removeNote(track, note) {
    const idx = track.notes.indexOf(note);
    if (idx >= 0) track.notes.splice(idx, 1);
  }

  /* ═══════════════════════════════════════
     CANVAS  SETUP
     ═══════════════════════════════════════ */

  _initCanvas() {
    this._canvas   = document.getElementById('mm-canvas');
    this._pianoC   = document.getElementById('mm-piano');
    this._scrollEl = document.getElementById('mm-timeline');
    if (!this._canvas || !this._pianoC) return;
    this._ctx    = this._canvas.getContext('2d');
    this._pianoX = this._pianoC.getContext('2d');
    this._sizeCanvases();
  }

  _sizeCanvases() {
    const cw = this._cellW, ch = this._cellH, pw = this._pianoW;
    const gW = this._gridCols * (cw + GUTTER) + GUTTER;
    const gH = this._viewRows * (ch + GUTTER) + GUTTER;

    this._canvas.width  = gW;
    this._canvas.height = HEADER_H + gH;
    this._canvas.style.width  = gW + 'px';
    this._canvas.style.height = (HEADER_H + gH) + 'px';

    this._pianoC.width  = pw;
    this._pianoC.height = gH;
    this._pianoC.style.width  = pw + 'px';
    this._pianoC.style.height = gH + 'px';

    const spacer = document.getElementById('mm-piano-spacer');
    if (spacer) {
      spacer.style.height    = HEADER_H + 'px';
      spacer.style.minHeight = HEADER_H + 'px';
    }
  }

  _syncUI() {
    const $ = id => document.getElementById(id);
    const bpm = $('mm-bpm');  if (bpm) bpm.value = this.planetMusic.tempo || 100;
    const bd  = $('mm-bars-display');  if (bd)  bd.textContent = this._bars;
    const od  = $('mm-oct-display');   if (od)  od.textContent = this._octaves;
    const lt  = $('mm-loop-toggle');   if (lt)  lt.checked = this._loopEnabled;
    this._updateLoopLabel();
  }

  _updateLoopLabel() {
    const el = document.getElementById('mm-loop-info');
    if (!el) return;
    const s = Math.floor(this._loopStart / CELLS_PER_BAR) + 1;
    const e = Math.ceil(this._loopEnd / CELLS_PER_BAR);
    el.textContent = `${s} → ${e}`;
  }

  /* ═══════════════════════════════════════
     DRAWING  — MAIN  CANVAS
     ═══════════════════════════════════════ */

  _render() {
    const c = this._ctx;
    if (!c) return;
    c.clearRect(0, 0, this._canvas.width, this._canvas.height);
    c.fillStyle = this._theme.bg;
    c.fillRect(0, 0, this._canvas.width, this._canvas.height);

    this._drawLoopBar(c);
    this._drawBeatLabels(c);
    this._drawGrid(c);
    this._drawBarHighlight(c);
    this._drawAllNotes(c);
    this._drawDragGhost(c);
    this._drawPlayhead(c);
  }

  /* ── loop indicator in top strip ── */
  _drawLoopBar(c) {
    const t = this._theme;
    c.fillStyle = rgba(t.text, 0.025);
    c.fillRect(0, 0, this._canvas.width, LOOP_BAR_H);
    if (!this._loopEnabled) return;

    const x1 = this._c2x(this._loopStart);
    const x2 = this._c2x(this._loopEnd);

    // region fill
    c.fillStyle = rgba(t.accent, 0.18);
    c.fillRect(x1, 0, x2 - x1, LOOP_BAR_H);
    c.strokeStyle = rgba(t.accent, 0.5);
    c.lineWidth = 1;
    c.strokeRect(x1 + 0.5, 0.5, x2 - x1 - 1, LOOP_BAR_H - 1);

    // handles
    c.fillStyle = rgba(t.accent, 0.9);
    c.fillRect(x1, 0, 3, LOOP_BAR_H);
    c.fillRect(x2 - 3, 0, 3, LOOP_BAR_H);
  }

  /* ── bar / beat numbers (clickable) ── */
  _drawBeatLabels(c) {
    const t = this._theme;
    const y0 = LOOP_BAR_H;
    c.fillStyle = rgba(t.text, 0.025);
    c.fillRect(0, y0, this._canvas.width, HEADER_H - LOOP_BAR_H);

    c.textAlign = 'center';
    for (let col = 0; col < this._gridCols; col++) {
      const cx = this._c2x(col) + this._cellW / 2;
      if (col % CELLS_PER_BAR === 0) {
        const barIdx = col / CELLS_PER_BAR;
        const isSel  = barIdx === this._selectedBar;
        // selected bar number gets a pill background
        if (isSel) {
          const bx = this._c2x(col);
          const bw = CELLS_PER_BAR * (this._cellW + GUTTER);
          c.fillStyle = rgba(t.accent, 0.15);
          c.fillRect(bx, y0, bw, HEADER_H - LOOP_BAR_H);
          c.fillStyle = rgba(t.accent, 0.9);
          c.font = `500 11px ${t.mono}`;
        } else {
          c.fillStyle = rgba(t.text, 0.55);
          c.font = `10px ${t.mono}`;
        }
        c.fillText(String(barIdx + 1), cx, HEADER_H - 5);
      } else if (col % CELLS_PER_BEAT === 0) {
        c.fillStyle = rgba(t.text, 0.18);
        c.font = `10px ${t.mono}`;
        c.fillText('·', cx, HEADER_H - 5);
      }
    }
  }

  /* ── selected bar highlight ── */
  _drawBarHighlight(c) {
    if (this._selectedBar < 0) return;
    const x = this._c2x(this._selectedBar * CELLS_PER_BAR);
    const w = CELLS_PER_BAR * (this._cellW + GUTTER);
    // subtle column highlight
    c.fillStyle = rgba(this._theme.accent, 0.04);
    c.fillRect(x, HEADER_H, w, this._canvas.height - HEADER_H);
    // top/bottom border accent
    c.strokeStyle = rgba(this._theme.accent, 0.2);
    c.lineWidth = 1.5;
    c.beginPath();
    c.moveTo(x, HEADER_H); c.lineTo(x, this._canvas.height);
    c.moveTo(x + w, HEADER_H); c.lineTo(x + w, this._canvas.height);
    c.stroke();
  }

  /* ── cell backgrounds ── */
  _drawGrid(c) {
    const perc = this._activeIsPerc;
    for (let row = 0; row < this._viewRows; row++) {
      const note  = this.scaleNotes[row] || '';
      const sharp = perc ? row % 2 === 1 : note.includes('#');
      for (let col = 0; col < this._gridCols; col++) {
        const x = this._c2x(col), y = this._r2y(row);
        const bar0  = col % CELLS_PER_BAR  === 0;
        const beat0 = col % CELLS_PER_BEAT === 0;
        const inLoop = !this._loopEnabled ||
          (col >= this._loopStart && col < this._loopEnd);

        let a = sharp ? 0.035 : 0.02;
        if (bar0) a += 0.04; else if (beat0) a += 0.02;
        if (!inLoop) a *= 0.35;

        c.fillStyle = rgba(this._theme.text, a);
        c.fillRect(x, y, this._cellW, this._cellH);
      }
    }
    // bar lines
    c.strokeStyle = rgba(this._theme.text, 0.07);
    c.lineWidth = 1;
    for (let col = 0; col <= this._gridCols; col += CELLS_PER_BAR) {
      const x = this._c2x(col) - 0.5;
      c.beginPath(); c.moveTo(x, HEADER_H); c.lineTo(x, this._canvas.height); c.stroke();
    }
  }

  /* ── all notes across tracks ── */
  _drawAllNotes(c) {
    // draw inactive tracks first, active track on top
    const order = [];
    for (let i = 0; i < this.tracks.length; i++) {
      if (i !== this.activeTrackIndex) order.push(i);
    }
    order.push(this.activeTrackIndex);

    const percView = this._activeIsPerc;
    for (const ti of order) {
      // drum rows and pitch rows are different spaces — only draw tracks
      // that share the current view's row semantics
      if (this._isPercTrack(ti) !== percView) continue;
      const active = ti === this.activeTrackIndex;
      const stroke = tCol(ti, active ? 1 : 0.35);
      for (const n of this.tracks[ti].notes) {
        // softer accents render more transparent
        const vel  = n.vel ?? 1;
        const fill = tCol(ti, (active ? 0.8 : 0.2) * (0.35 + 0.65 * vel));
        this._drawNote(c, n, fill, stroke, active);
      }
    }
  }

  _drawNote(c, n, fill, stroke, label) {
    const x = this._c2x(n.col) + 1;
    const y = this._r2y(n.row) + 1;
    const w = n.dur * (this._cellW + GUTTER) - GUTTER - 2;
    const h = this._cellH - 2;
    const r = 3;

    this._rr(c, x, y, w, h, r);
    c.fillStyle = fill; c.fill();
    c.strokeStyle = stroke; c.lineWidth = 1; c.stroke();

    if (label && w > 28) {
      c.fillStyle = rgba(this._theme.text, 0.65);
      c.font = `8px ${this._theme.mono}`; c.textAlign = 'left';
      c.fillText(this._rowLabel(n.row), x + 3, y + h - 3);
    }
  }

  /* ── ghost preview while dragging ── */
  _drawDragGhost(c) {
    if (!this._drag || this._drag.mode !== 'create' || !this._drag.note) return;
    c.globalAlpha = 0.45;
    this._drawNote(c, this._drag.note,
      tCol(this.activeTrackIndex, 0.55),
      tCol(this.activeTrackIndex, 0.85), false);
    c.globalAlpha = 1;
  }

  /* ── playhead column ── */
  _drawPlayhead(c) {
    if (!this._playing) return;
    const x = this._c2x(this._playCol);

    c.fillStyle = rgba(this._theme.text, 0.055);
    c.fillRect(x, HEADER_H, this._cellW, this._canvas.height - HEADER_H);

    c.strokeStyle = rgba(this._theme.text, 0.65);
    c.lineWidth = 2;
    c.beginPath(); c.moveTo(x, HEADER_H); c.lineTo(x, this._canvas.height); c.stroke();

    // triangle marker
    c.fillStyle = rgba(this._theme.text, 0.75);
    c.beginPath();
    c.moveTo(x, HEADER_H);
    c.lineTo(x + 5, HEADER_H - 7);
    c.lineTo(x - 5, HEADER_H - 7);
    c.closePath(); c.fill();
  }

  /* ═══════════════════════════════════════
     DRAWING  — PIANO  SIDEBAR
     ═══════════════════════════════════════ */

  _renderPiano() {
    const c = this._pianoX;
    if (!c) return;
    const t = this._theme;
    const w = this._pianoW, h = this._pianoC.height;
    const ch = this._cellH;
    c.clearRect(0, 0, w, h);
    c.fillStyle = t.bg;
    c.fillRect(0, 0, w, h);

    const perc = this._activeIsPerc;
    for (let row = 0; row < this._viewRows; row++) {
      const note  = this._rowLabel(row);
      const sharp = perc ? row % 2 === 1 : note.includes('#');
      const y = GUTTER + row * (ch + GUTTER);

      c.fillStyle = rgba(t.text, sharp ? 0.08 : 0.045);
      c.fillRect(0, y, w, ch);

      c.fillStyle = rgba(t.text, sharp ? 0.4 : 0.55);
      c.font = _isMobile() ? `8px ${t.mono}` : `9px ${t.mono}`; c.textAlign = 'right';
      c.fillText(note, w - 4, y + ch / 2 + 3);
    }

    // right edge
    c.strokeStyle = rgba(t.accent, 0.16);
    c.lineWidth = 1;
    c.beginPath(); c.moveTo(w - 0.5, 0); c.lineTo(w - 0.5, h); c.stroke();
  }

  /* ═══════════════════════════════════════
     COORDINATE  HELPERS
     ═══════════════════════════════════════ */

  _c2x(col) { return GUTTER + col * (this._cellW + GUTTER); }
  _r2y(row) { return HEADER_H + GUTTER + row * (this._cellH + GUTTER); }
  _x2c(x)   { return Math.floor((x - GUTTER) / (this._cellW + GUTTER)); }
  _y2r(y)   { return Math.floor((y - HEADER_H - GUTTER) / (this._cellH + GUTTER)); }
  _snapC(c)  { return Math.floor(c / this._snap) * this._snap; }

  /** rounded-rect path */
  _rr(ctx, x, y, w, h, r) {
    if (w < 0) w = 0;
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);   ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);   ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);       ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  /** pixel coords from mouse/touch event → canvas coords (handles CSS scaling) */
  _canvasXY(e) {
    const r = this._canvas.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (this._canvas.width  / r.width),
      y: (e.clientY - r.top)  * (this._canvas.height / r.height),
    };
  }

  /** Synthesize a mouse-event-like object from a Touch */
  _touchToMouse(touch) {
    return { clientX: touch.clientX, clientY: touch.clientY, button: 0 };
  }

  /* ═══════════════════════════════════════
     INPUT  HANDLING
     ═══════════════════════════════════════ */

  _bindEvents() {
    if (this._bound) return;
    this._bound = true;

    /* canvas pointer (mouse) */
    this._canvas.addEventListener('mousedown',   e => this._onDown(e));
    this._canvas.addEventListener('mousemove',   e => this._onMove(e));
    window.addEventListener('mouseup',           e => this._onUp(e));
    this._canvas.addEventListener('contextmenu', e => e.preventDefault());

    /* canvas pointer (touch — mobile) */
    this._canvas.style.touchAction = 'none';        // we manage scrolling ourselves

    this._canvas.addEventListener('touchstart', e => {
      e.preventDefault();
      // Two fingers → pan the grid (cancels any in-progress draw)
      if (e.touches.length >= 2) {
        this._drag = null;
        this._pan = {
          x:  (e.touches[0].clientX + e.touches[1].clientX) / 2,
          y:  (e.touches[0].clientY + e.touches[1].clientY) / 2,
          sl: this._scrollEl?.scrollLeft || 0,
          st: this._scrollEl?.scrollTop  || 0,
        };
        this._render();
        return;
      }
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      const synth = this._touchToMouse(t);

      if (this._eraseMode) {
        // In erase mode: single tap instantly deletes
        const { x, y } = this._canvasXY(synth);
        // Allow bar selection from header even in erase mode
        if (y >= LOOP_BAR_H && y < HEADER_H) {
          this._onDown(synth);
        } else if (y >= HEADER_H) {
          const col = this._x2c(x), row = this._y2r(y);
          const track = this.tracks[this.activeTrackIndex];
          if (track) {
            const hit = this._noteAt(track, col, row);
            if (hit) { this._pushUndo(); this._removeNote(track, hit); this._render(); }
          }
          this._drag = { mode: 'delete', startRow: row };
          if (navigator.vibrate) navigator.vibrate(20);
        }
      } else {
        // In draw mode: start creating a note
        this._onDown(synth);
      }
    }, { passive: false });

    this._canvas.addEventListener('touchmove', e => {
      e.preventDefault();
      if (this._pan && e.touches.length >= 2 && this._scrollEl) {
        const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        this._scrollEl.scrollLeft = this._pan.sl - (mx - this._pan.x);
        this._scrollEl.scrollTop  = this._pan.st - (my - this._pan.y);
        return;
      }
      if (e.touches.length !== 1) return;
      this._onMove(this._touchToMouse(e.touches[0]));
    }, { passive: false });

    this._canvas.addEventListener('touchend', e => {
      e.preventDefault();
      if (this._pan) {
        if (e.touches.length < 2) this._pan = null;
        return;
      }
      this._onUp();
    }, { passive: false });

    this._canvas.addEventListener('touchcancel', () => {
      this._drag = null;
      this._pan  = null;
      this._render();
    });

    /* toolbar wiring */
    const $ = id => document.getElementById(id);
    $('mm-play')?.addEventListener('click', async () => {
      // Ensure audio context is running (required on mobile after user gesture)
      if (Tone.context.state !== 'running') {
        await Tone.start();
      }
      this._playing ? this.stop() : this.play();
    });
    $('mm-stop')?.addEventListener('click', () => this.stop());

    /* Draw / Erase mode toggle */
    $('mm-mode-toggle')?.addEventListener('click', () => {
      this._eraseMode = !this._eraseMode;
      const btn = $('mm-mode-toggle');
      if (btn) {
        btn.innerHTML = this._eraseMode ? `${icon('eraser')} Erase` : `${icon('pencil')} Draw`;
        btn.classList.toggle('erase-active', this._eraseMode);
      }
      // Change canvas cursor
      if (this._canvas) {
        this._canvas.style.cursor = this._eraseMode ? 'not-allowed' : 'crosshair';
      }
    });

    /* Settings panel toggle (mobile) */
    $('mm-settings-toggle')?.addEventListener('click', () => {
      this._settingsOpen = !this._settingsOpen;
      const panel = $('mm-toolbar-settings');
      const btn = $('mm-settings-toggle');
      if (panel) panel.classList.toggle('open', this._settingsOpen);
      if (btn) btn.classList.toggle('active', this._settingsOpen);
    });

    $('mm-bars-minus')?.addEventListener('click', () => this._setBars(this._bars - 1));
    $('mm-bars-plus')?.addEventListener('click',  () => this._setBars(this._bars + 1));
    $('mm-oct-minus')?.addEventListener('click',  () => this._setOctaves(this._octaves - 1));
    $('mm-oct-plus')?.addEventListener('click',   () => this._setOctaves(this._octaves + 1));

    $('mm-snap')?.addEventListener('change', e => {
      this._snap = parseInt(e.target.value) || 4;
    });
    $('mm-loop-toggle')?.addEventListener('change', e => {
      this._loopEnabled = e.target.checked;
      this._render();
    });
    $('mm-clear-track')?.addEventListener('click', () => {
      const t = this.tracks[this.activeTrackIndex];
      if (t && t.notes.length) { this._pushUndo(); t.notes = []; this._render(); }
    });
    $('mm-undo')?.addEventListener('click', () => this.undo());
    $('mm-redo')?.addEventListener('click', () => this.redo());

    /* bar action buttons */
    $('mm-bar-copy')?.addEventListener('click', () => this._copySelectedBar());
    $('mm-bar-paste')?.addEventListener('click', () => this._pasteToSelectedBar());
    $('mm-bar-clear')?.addEventListener('click', () => this._clearSelectedBar());
    $('mm-bar-dup')?.addEventListener('click', () => this._duplicateSelectedBar());
    $('mm-bar-insert')?.addEventListener('click', () => this._insertEmptyBarAfter());
    $('mm-bar-delete')?.addEventListener('click', () => this._deleteSelectedBar());
    $('mm-bar-deselect')?.addEventListener('click', () => {
      this._selectedBar = -1; this._updateBarActions(); this._render();
    });

    $('mm-bpm')?.addEventListener('change', e => {
      if (this._playing) Tone.getTransport().bpm.value = parseInt(e.target.value) || 100;
    });

    /* keyboard: space = play/stop, bar operations */
    this._keyHandler = e => {
      if (document.getElementById('music-maker')?.classList.contains('hidden')) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;

      if (e.code === 'Space') {
        e.preventDefault();
        this._playing ? this.stop() : this.play();
        return;
      }
      if (e.code === 'Escape') {
        this._selectedBar = -1;
        this._updateBarActions(); this._render();
        return;
      }
      // Bar operations with Ctrl/Cmd
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.code === 'KeyZ') {
        e.preventDefault();
        e.shiftKey ? this.redo() : this.undo();
        return;
      }
      if (mod && e.code === 'KeyY') { e.preventDefault(); this.redo(); return; }
      if (this._selectedBar >= 0) {
        if (mod && e.code === 'KeyC') { e.preventDefault(); this._copySelectedBar(); return; }
        if (mod && e.code === 'KeyV') { e.preventDefault(); this._pasteToSelectedBar(); return; }
        if (mod && e.code === 'KeyD') { e.preventDefault(); this._duplicateSelectedBar(); return; }
        if (e.code === 'Delete' || e.code === 'Backspace') {
          e.preventDefault();
          if (e.shiftKey) this._deleteSelectedBar();
          else this._clearSelectedBar();
          return;
        }
      }
    };
    window.addEventListener('keydown', this._keyHandler);
  }

  /* ── mouse down ── */
  _onDown(e) {
    const { x, y } = this._canvasXY(e);

    // loop-bar region?
    if (y < LOOP_BAR_H && this._loopEnabled) {
      this._startLoopDrag(x);
      return;
    }
    // bar header area → select bar
    if (y >= LOOP_BAR_H && y < HEADER_H) {
      const col = this._x2c(x);
      if (col >= 0 && col < this._gridCols) {
        const barIdx = Math.floor(col / CELLS_PER_BAR);
        this._selectBar(barIdx);
      }
      return;
    }

    const col = this._x2c(x);
    const row = this._y2r(y);
    if (col < 0 || col >= this._gridCols || row < 0 || row >= this._viewRows) return;

    const track = this.tracks[this.activeTrackIndex];
    if (!track) return;

    const hit = this._noteAt(track, col, row);

    // Alt+click a note → cycle its accent level (full / soft / ghost)
    if (hit && e.altKey) {
      this._pushUndo();
      hit.vel = (hit.vel ?? 1) >= 1 ? 0.7 : hit.vel >= 0.7 ? 0.4 : 1;
      this._render();
      return;
    }

    // right-click OR click on existing note → erase
    if (e.button === 2 || hit) {
      this._pushUndo();
      if (hit) this._removeNote(track, hit);
      this._drag = { mode: 'delete', startRow: row };
      this._render();
      return;
    }

    // left-click on empty → start creating a note
    const sc = this._snapC(col);
    this._drag = {
      mode: 'create',
      startCol: sc,
      startRow: row,
      note: { col: sc, row, dur: Math.max(1, this._snap) },
    };
    this._render();
  }

  /* ── mouse move ── */
  _onMove(e) {
    if (!this._drag) return;
    const { x, y } = this._canvasXY(e);
    const d = this._drag;

    if (d.mode === 'create' && d.note) {
      const rawCol = Math.max(0, Math.min(this._x2c(x), this._gridCols - 1));
      const snappedEnd = this._snapC(rawCol) + this._snap;
      d.note.dur = Math.max(this._snap,
        Math.min(snappedEnd - d.startCol, this._gridCols - d.startCol));
      this._render();

    } else if (d.mode === 'delete') {
      const col = this._x2c(x), row = this._y2r(y);
      const track = this.tracks[this.activeTrackIndex];
      if (track && row >= 0 && row < this._viewRows) {
        const hit = this._noteAt(track, col, row);
        if (hit) { this._removeNote(track, hit); this._render(); }
      }

    } else if (d.mode === 'loop-start') {
      let c = Math.max(0, this._snapC(this._x2c(x)));
      if (c < this._loopEnd - this._snap) {
        this._loopStart = c; this._updateLoopLabel(); this._render();
      }
    } else if (d.mode === 'loop-end') {
      let c = Math.min(this._gridCols,
        Math.ceil((this._x2c(x) + 1) / this._snap) * this._snap);
      if (c > this._loopStart + this._snap) {
        this._loopEnd = c; this._updateLoopLabel(); this._render();
      }
    } else if (d.mode === 'loop-body') {
      const c   = this._x2c(x);
      const len = this._loopEnd - this._loopStart;
      let ns = this._snapC(c - d.offset);
      ns = Math.max(0, Math.min(ns, this._gridCols - len));
      this._loopStart = ns;
      this._loopEnd   = ns + len;
      this._updateLoopLabel(); this._render();
    }
  }

  /* ── mouse up ── */
  _onUp() {
    if (!this._drag) return;
    if (this._drag.mode === 'create' && this._drag.note) {
      const track = this.tracks[this.activeTrackIndex];
      if (track) {
        const n = this._drag.note;
        this._pushUndo();
        this._addNote(track, n.col, n.row, n.dur);
        this._previewNote(this.activeTrackIndex, n.row, n.dur);
      }
    }
    this._drag = null;
    this._render();
  }

  /* ── loop handle hit-testing ── */
  _startLoopDrag(x) {
    const sx = this._c2x(this._loopStart);
    const ex = this._c2x(this._loopEnd);

    if (Math.abs(x - sx) < 10) {
      this._drag = { mode: 'loop-start' };
    } else if (Math.abs(x - ex) < 10) {
      this._drag = { mode: 'loop-end' };
    } else if (x > sx && x < ex) {
      this._drag = { mode: 'loop-body', offset: this._x2c(x) - this._loopStart };
    } else {
      // click outside loop → snap nearest edge
      const col = this._snapC(this._x2c(x));
      if (col < (this._loopStart + this._loopEnd) / 2) {
        this._loopStart = col;
      } else {
        this._loopEnd = Math.min(
          Math.ceil((col + 1) / this._snap) * this._snap, this._gridCols);
      }
      this._updateLoopLabel(); this._render();
    }
  }

  /* ═══════════════════════════════════════
     GRID  RESIZING
     ═══════════════════════════════════════ */

  _setBars(n) {
    n = Math.max(MIN_BARS, Math.min(MAX_BARS, n));
    if (n === this._bars) return;
    this._pushUndo();
    const oldBars = this._bars;
    const growing = n > oldBars;
    this._bars     = n;
    this._gridCols = n * CELLS_PER_BAR;

    // clamp loop
    this._loopEnd   = Math.min(this._loopEnd, this._gridCols);
    this._loopStart = Math.min(this._loopStart, Math.max(0, this._loopEnd - CELLS_PER_BAR));

    if (growing) {
      // Auto-copy last bar's content into each new bar
      const srcBar = oldBars - 1;
      for (let b = oldBars; b < n; b++) {
        this._copyBarContent(srcBar, b);
      }
      this._showToast(`+${n - oldBars} bar${n - oldBars > 1 ? 's' : ''} (copied from bar ${oldBars})`);
    } else {
      // prune notes beyond the new grid
      for (const t of this.tracks) {
        t.notes = t.notes.filter(nt => nt.col < this._gridCols);
        for (const nt of t.notes) {
          if (nt.col + nt.dur > this._gridCols) nt.dur = this._gridCols - nt.col;
        }
      }
    }

    // clamp selection
    if (this._selectedBar >= n) this._selectedBar = -1;

    const d = document.getElementById('mm-bars-display');
    if (d) d.textContent = n;
    this._sizeCanvases(); this._updateLoopLabel(); this._updateBarActions(); this._render(); this._renderPiano();
  }

  _setOctaves(n) {
    n = Math.max(MIN_OCT, Math.min(MAX_OCT, n));
    if (n === this._octaves) return;
    this._pushUndo();
    this._octaves = n;
    this._buildScale();
    for (const t of this.tracks) {
      if (t.instrumentRole === 'percussion') continue;  // drum rows are octave-independent
      t.notes = t.notes.filter(nt => nt.row < this._gridRows);
    }

    const d = document.getElementById('mm-oct-display');
    if (d) d.textContent = n;
    this._sizeCanvases(); this._render(); this._renderPiano();
  }

  /* ═══════════════════════════════════════
     BAR  OPERATIONS
     ═══════════════════════════════════════ */

  /** Select a bar by 0-based index (-1 = deselect) */
  _selectBar(barIdx) {
    if (barIdx === this._selectedBar) {
      this._selectedBar = -1; // toggle off
    } else {
      this._selectedBar = barIdx;
    }
    this._updateBarActions();
    this._render();
  }

  /** Get all notes in a given bar (0-based) across all tracks */
  _getBarNotes(barIdx) {
    const start = barIdx * CELLS_PER_BAR;
    const end   = start + CELLS_PER_BAR;
    return this.tracks.map(t =>
      t.notes.filter(n => n.col >= start && n.col < end)
              .map(n => ({ row: n.row, col: n.col - start, dur: Math.min(n.dur, end - n.col), vel: n.vel }))
    );
  }

  /** Copy notes from srcBar into destBar across all tracks (overwrites dest) */
  _copyBarContent(srcBar, destBar) {
    const srcNotes = this._getBarNotes(srcBar);
    const destStart = destBar * CELLS_PER_BAR;
    const destEnd   = destStart + CELLS_PER_BAR;
    for (let ti = 0; ti < this.tracks.length; ti++) {
      // remove existing notes in dest bar
      this.tracks[ti].notes = this.tracks[ti].notes.filter(
        n => n.col + n.dur <= destStart || n.col >= destEnd
      );
      // paste source notes shifted to dest
      for (const sn of srcNotes[ti]) {
        this.tracks[ti].notes.push({ row: sn.row, col: sn.col + destStart, dur: sn.dur, vel: sn.vel });
      }
    }
  }

  /** Copy selected bar to clipboard */
  _copySelectedBar() {
    if (this._selectedBar < 0) return;
    this._clipboardBar = this._getBarNotes(this._selectedBar);
    this._showToast(`Bar ${this._selectedBar + 1} copied`);
    this._updateBarActions();
  }

  /** Paste clipboard into selected bar */
  _pasteToSelectedBar() {
    if (this._selectedBar < 0 || !this._clipboardBar) return;
    this._pushUndo();
    const destStart = this._selectedBar * CELLS_PER_BAR;
    const destEnd   = destStart + CELLS_PER_BAR;
    for (let ti = 0; ti < this.tracks.length; ti++) {
      if (ti >= this._clipboardBar.length) break;
      // clear dest bar
      this.tracks[ti].notes = this.tracks[ti].notes.filter(
        n => n.col + n.dur <= destStart || n.col >= destEnd
      );
      // paste
      for (const cn of this._clipboardBar[ti]) {
        this.tracks[ti].notes.push({ row: cn.row, col: cn.col + destStart, dur: cn.dur, vel: cn.vel });
      }
    }
    this._showToast(`Pasted to bar ${this._selectedBar + 1}`);
    this._render();
  }

  /** Clear all notes in selected bar */
  _clearSelectedBar() {
    if (this._selectedBar < 0) return;
    this._pushUndo();
    const start = this._selectedBar * CELLS_PER_BAR;
    const end   = start + CELLS_PER_BAR;
    for (const t of this.tracks) {
      t.notes = t.notes.filter(n => n.col + n.dur <= start || n.col >= end);
    }
    this._showToast(`Bar ${this._selectedBar + 1} cleared`);
    this._render();
  }

  /** Duplicate selected bar → insert a copy right after it, shifting subsequent content */
  _duplicateSelectedBar() {
    if (this._selectedBar < 0 || this._bars >= MAX_BARS) return;
    this._pushUndo();
    const srcBar = this._selectedBar;
    const srcNotes = this._getBarNotes(srcBar);
    const insertCol = (srcBar + 1) * CELLS_PER_BAR;

    // grow grid by 1 bar
    this._bars += 1;
    this._gridCols = this._bars * CELLS_PER_BAR;

    // shift all notes at/after insertCol right by CELLS_PER_BAR
    for (const t of this.tracks) {
      for (const n of t.notes) {
        if (n.col >= insertCol) n.col += CELLS_PER_BAR;
      }
    }

    // insert copied notes
    for (let ti = 0; ti < this.tracks.length; ti++) {
      for (const sn of srcNotes[ti]) {
        this.tracks[ti].notes.push({ row: sn.row, col: sn.col + insertCol, dur: sn.dur, vel: sn.vel });
      }
    }

    // expand loop if it was at the end
    if (this._loopEnd === (this._bars - 1) * CELLS_PER_BAR) {
      this._loopEnd = this._gridCols;
    }

    this._selectedBar = srcBar + 1; // select the new copy

    const d = document.getElementById('mm-bars-display');
    if (d) d.textContent = this._bars;
    this._showToast(`Bar ${srcBar + 1} duplicated`);
    this._sizeCanvases(); this._updateLoopLabel(); this._updateBarActions(); this._render(); this._renderPiano();
  }

  /** Insert an empty bar after the selected bar */
  _insertEmptyBarAfter() {
    if (this._bars >= MAX_BARS) return;
    this._pushUndo();
    const afterBar = this._selectedBar >= 0 ? this._selectedBar : this._bars - 1;
    const insertCol = (afterBar + 1) * CELLS_PER_BAR;

    this._bars += 1;
    this._gridCols = this._bars * CELLS_PER_BAR;

    // shift notes right
    for (const t of this.tracks) {
      for (const n of t.notes) {
        if (n.col >= insertCol) n.col += CELLS_PER_BAR;
      }
    }

    if (this._loopEnd === (this._bars - 1) * CELLS_PER_BAR) {
      this._loopEnd = this._gridCols;
    }

    this._selectedBar = afterBar + 1;

    const d = document.getElementById('mm-bars-display');
    if (d) d.textContent = this._bars;
    this._showToast(`Empty bar inserted at ${afterBar + 2}`);
    this._sizeCanvases(); this._updateLoopLabel(); this._updateBarActions(); this._render(); this._renderPiano();
  }

  /** Delete the selected bar entirely, shifting subsequent bars left */
  _deleteSelectedBar() {
    if (this._selectedBar < 0 || this._bars <= MIN_BARS) return;
    this._pushUndo();
    const bar = this._selectedBar;
    const start = bar * CELLS_PER_BAR;
    const end   = start + CELLS_PER_BAR;

    for (const t of this.tracks) {
      // remove notes in this bar
      t.notes = t.notes.filter(n => n.col + n.dur <= start || n.col >= end);
      // shift notes after this bar left
      for (const n of t.notes) {
        if (n.col >= end) n.col -= CELLS_PER_BAR;
      }
    }

    this._bars -= 1;
    this._gridCols = this._bars * CELLS_PER_BAR;

    // clamp loop
    this._loopEnd   = Math.min(this._loopEnd, this._gridCols);
    this._loopStart = Math.min(this._loopStart, Math.max(0, this._loopEnd - CELLS_PER_BAR));

    // clamp selection
    if (this._selectedBar >= this._bars) this._selectedBar = this._bars - 1;

    const d = document.getElementById('mm-bars-display');
    if (d) d.textContent = this._bars;
    this._showToast(`Bar ${bar + 1} deleted`);
    this._sizeCanvases(); this._updateLoopLabel(); this._updateBarActions(); this._render(); this._renderPiano();
  }

  /** Show/hide bar action buttons depending on selection state */
  _updateBarActions() {
    const strip = document.getElementById('mm-bar-actions');
    if (!strip) return;
    const hasSel = this._selectedBar >= 0;
    const hasClip = !!this._clipboardBar;
    strip.classList.toggle('hidden', !hasSel);
    const label = strip.querySelector('.mm-bar-label');
    if (label && hasSel) label.textContent = `Bar ${this._selectedBar + 1}`;
    const paste = strip.querySelector('#mm-bar-paste');
    if (paste) paste.classList.toggle('disabled', !hasClip);
    const del = strip.querySelector('#mm-bar-delete');
    if (del) del.classList.toggle('disabled', this._bars <= MIN_BARS);
  }

  /** Show a brief toast — routed through the shared notification stack,
   *  which sits at --z-toast so it renders above the Music Maker modal. */
  _showToast(msg) {
    events.emit(EVT.NOTIFICATION, msg);
  }

  /* ═══════════════════════════════════════
     AUDIO  /  SYNTHS
     ═══════════════════════════════════════ */

  _createSynths() {
    this._disposeSynths();
    const dest = this._audio?.filterNode || Tone.getDestination();

    this.tracks.forEach(t => {
      const role = HARMONY.INSTRUMENT_ROLES.find(r => r.id === t.instrumentRole);

      if (t.instrumentRole === 'percussion') {
        const g = new Tone.Gain(this._trackGain(t)).connect(dest);
        const s = createDrumKit(g, -8);
        this._synths.push({ synth: s, gain: g, isPerc: true });
      } else if (role?.sampleMapping) {
        const g = new Tone.Gain(this._trackGain(t)).connect(dest);
        const s = createRoleSampler(role, -6, g);
        this._synths.push({ synth: s, gain: g, isPerc: false });
      } else {
        const g = new Tone.Gain(this._trackGain(t)).connect(dest);
        const s = new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'triangle' },
          envelope: { attack: 0.02, decay: 0.2, sustain: 0.3, release: 0.4 },
          volume: -8,
        }).connect(g);
        this._synths.push({ synth: s, gain: g, isPerc: false });
      }
    });
  }

  _disposeSynths() {
    for (const s of this._synths) {
      try { s.synth.dispose(); } catch (_) {}
      try { s.gain.dispose(); } catch (_) {}
    }
    this._synths = [];
  }

  _previewNote(ti, row, dur = 1) {
    const si = this._synths[ti];
    if (!si) return;
    const note = si.isPerc ? drumNoteForRow(row) : this.scaleNotes[row];
    if (!note) return;
    const d = dur <= 2 ? '16n' : dur <= 4 ? '8n' : dur <= 8 ? '4n' : '2n';
    try {
      si.synth.triggerAttackRelease(note, si.isPerc ? '16n' : d);
    } catch (_) {}
  }

  /* ═══════════════════════════════════════
     PLAYBACK
     ═══════════════════════════════════════ */

  play() {
    if (this._playing) this.stop();

    const bpm = parseInt(document.getElementById('mm-bpm')?.value) || 100;
    const transport = Tone.getTransport();

    // Fully reset the transport so playback always starts fresh
    transport.stop();
    transport.cancel();       // clear any lingering scheduled events
    transport.position = 0;   // rewind to beat 0
    transport.bpm.value = bpm;

    this._playing = true;

    const startCol = this._loopEnabled ? this._loopStart : 0;
    const endCol   = this._loopEnabled ? this._loopEnd   : this._gridCols;
    this._playCol  = startCol;

    const cols = [];
    for (let c = startCol; c < endCol; c++) cols.push(c);

    this._seq = new Tone.Sequence((time, col) => {
      this._playCol = col;

      const anySolo = this.tracks.some(t => t.solo);
      for (let ti = 0; ti < this.tracks.length; ti++) {
        const t  = this.tracks[ti];
        if (t.muted || (anySolo && !t.solo)) continue;
        const si = this._synths[ti];
        if (!si) continue;

        for (const n of t.notes) {
          if (n.col !== col) continue;              // trigger only at note START
          const name = si.isPerc ? drumNoteForRow(n.row) : this.scaleNotes[n.row];
          if (!name) continue;
          const durSec = (n.dur * 60) / (bpm * CELLS_PER_BEAT);
          try {
            si.synth.triggerAttackRelease(name, si.isPerc ? '16n' : durSec, time, n.vel ?? 1);
          } catch (_) {}
        }
      }
    }, cols, '16n');

    this._seq.loop = true;
    this._seq.start(0);
    transport.start();

    this._animate();

    const pb = document.getElementById('mm-play');
    if (pb) pb.innerHTML = icon('pause');
  }

  stop() {
    this._playing = false;
    this._playCol = this._loopEnabled ? this._loopStart : 0;

    if (this._seq) {
      this._seq.stop();
      this._seq.dispose();
      this._seq = null;
    }

    // Actually stop the transport so next play() starts clean
    const transport = Tone.getTransport();
    transport.stop();
    transport.cancel();
    transport.position = 0;

    if (this._animId) {
      cancelAnimationFrame(this._animId);
      this._animId = null;
    }
    this._render();

    const pb = document.getElementById('mm-play');
    if (pb) pb.innerHTML = icon('play');
  }

  _animate() {
    if (!this._playing) return;
    this._render();

    // auto-scroll to keep playhead visible
    if (this._scrollEl) {
      const px = this._c2x(this._playCol);
      const sl = this._scrollEl.scrollLeft;
      const vw = this._scrollEl.clientWidth;
      if (px < sl || px > sl + vw - this._pianoW - 50) {
        this._scrollEl.scrollLeft = Math.max(0, px - 20);
      }
    }

    this._animId = requestAnimationFrame(() => this._animate());
  }

  /* ═══════════════════════════════════════
     SERIALIZATION
     ═══════════════════════════════════════ */

  serialize() {
    return {
      version: 2,
      tracks: this.tracks.map(t => ({
        speciesId:      t.speciesId,
        name:           t.name,
        instrumentRole: t.instrumentRole,
        volume:         t.volume ?? 0.8,
        notes: t.notes.map(n => {
          const out = { col: n.col, row: n.row, dur: n.dur };
          if (n.vel != null && n.vel !== 1) out.v = n.vel;
          return out;
        }),
      })),
      bpm:         parseInt(document.getElementById('mm-bpm')?.value) || 100,
      bars:        this._bars,
      octaves:     this._octaves,
      loopStart:   this._loopStart,
      loopEnd:     this._loopEnd,
      loopEnabled: this._loopEnabled,
      key:         this.planetMusic?.root      || 'C',
      scaleName:   this.planetMusic?.scaleName || 'pentatonic',
      octave:      this.planetMusic?.octave    || 3,
    };
  }

  /**
   * Load a saved composition.
   */
  deserialize(data) {
    if (!data?.tracks) return;

    // restore grid settings
    if (data.bars) {
      this._bars     = Math.max(MIN_BARS, Math.min(MAX_BARS, data.bars));
      this._gridCols = this._bars * CELLS_PER_BAR;
    }
    if (data.octaves) {
      this._octaves = Math.max(MIN_OCT, Math.min(MAX_OCT, data.octaves));
      this._buildScale();
    }
    if (data.loopStart   !== undefined) this._loopStart   = data.loopStart;
    if (data.loopEnd     !== undefined) this._loopEnd     = Math.min(data.loopEnd, this._gridCols);
    if (data.loopEnabled !== undefined) this._loopEnabled = data.loopEnabled;

    // load notes per track
    for (const st of data.tracks) {
      let track = this.tracks.find(t => t.speciesId && t.speciesId === st.speciesId);
      if (!track) track = this.tracks.find(t => t.instrumentRole === st.instrumentRole);
      if (!track || !Array.isArray(st.notes) || st.notes.length === 0) continue;

      const maxRow = track.instrumentRole === 'percussion'
        ? DRUM_LANES.length - 1
        : Infinity;
      if (typeof st.volume === 'number') track.volume = st.volume;
      track.notes = st.notes.map(n => ({
        col: n.col, row: Math.min(n.row, maxRow), dur: n.dur || 1,
        vel: n.v ?? n.vel ?? 1,
      }));
    }

    // apply restored volumes to the already-created gain nodes + sliders
    this._synths.forEach((si, i) => {
      const t = this.tracks[i];
      if (si && t) si.gain.gain.value = this._trackGain(t);
    });
    this._populateTrackLabels();

    this._syncUI();
    this._sizeCanvases();
    this._render();
    this._renderPiano();
  }
}
